import { useEffect, useRef } from 'react';
import { FillLayer, GeoJSONSource, LineLayer } from 'mapbox-gl';

import { color } from '@strava/ui-tokens/js';
import { useMap } from './useMap';
import { useMapStyle } from './useMapStyle';
import { getCircleCoords } from './utils';

type PolygonCircleStyle = {
  fillColor?: string;
  fillOpacity?: number;
  strokeColor?: string;
  strokeOpacity?: number;
  strokeWeight?: number;
  strokeOffset?: number;
};

export type PolygonCircleProps = {
  /**
   * Unique ID of the layer.
   */
  id: string;

  /**
   * Latitude of the center of the circle.
   */
  lat: number;

  /**
   * Longitude  of the center of the circle.
   */
  lng: number;

  /**
   * Radius of the circle in meters.
   */
  radius: number;

  /**
   * Style object of the circle.
   */
  style: PolygonCircleStyle;
};

export const polygonCircleDefaultStyle: PolygonCircleStyle = {
  fillColor: color.colorCoreAsphalt,
  fillOpacity: 1,
  strokeColor: undefined,
  strokeOpacity: undefined,
  strokeWeight: undefined,
  strokeOffset: undefined
};

function strokeColor(style: PolygonCircleStyle) {
  return style.strokeColor ? style.strokeColor : style.fillColor;
}

function strokeOpacity(style: PolygonCircleStyle) {
  return style.strokeOpacity ? style.strokeOpacity : 1;
}

function strokeWeight(style: PolygonCircleStyle) {
  return style.strokeWeight ? style.strokeWeight : 0;
}

function strokeOffset(style: PolygonCircleStyle) {
  return style.strokeOffset ? style.strokeOffset : 0;
}

const PolygonCircle = ({
  id,
  lat,
  lng,
  radius,
  style: customStyle
}: PolygonCircleProps) => {
  const map = useMap();
  const mapStyle = useMapStyle();
  const isMountedRef = useRef(true);

  const style: PolygonCircleStyle = {
    ...polygonCircleDefaultStyle,
    ...customStyle
  };
  const sourceName = `${id}-source`;

  const layer: FillLayer = {
    id,
    type: 'fill',
    source: sourceName,
    paint: {
      'fill-color': style.fillColor,
      'fill-opacity': style.fillOpacity
    }
  };

  const layerOutline: LineLayer = {
    id: `${id}-outline`,
    type: 'line',
    source: sourceName,
    paint: {
      'line-color': strokeColor(style),
      'line-opacity': strokeOpacity(style),
      'line-width': strokeWeight(style),
      'line-offset': strokeOffset(style)
    }
  };

  const addLayer = () => {
    if (map && !map.getLayer(layer.id)) {
      map.addSource(sourceName, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      map.addLayer(layer).addLayer(layerOutline);
    }
  };

  const setData = () => {
    if (map?.getSource(sourceName)) {
      (map.getSource(sourceName) as GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [getCircleCoords([lng, lat], radius)]
            },
            properties: {
              lat,
              lng
            }
          }
        ]
      });
    }
  };

  useEffect(() => {
    if (!isMountedRef.current) {
      addLayer();
      setData();
    }
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapStyle]);

  useEffect(() => {
    if (!isMountedRef.current) {
      setData();
    }
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng, radius]);

  useEffect(() => {
    if (!isMountedRef.current) {
      map?.setPaintProperty(id, 'fill-color', style.fillColor);
      map?.setPaintProperty(id, 'fill-opacity', style.fillOpacity);
      map?.setPaintProperty(`${id}-outline`, 'line-color', strokeColor(style));
      map?.setPaintProperty(
        `${id}-outline`,
        'line-opacity',
        strokeOpacity(style)
      );
      map?.setPaintProperty(`${id}-outline`, 'line-width', strokeWeight(style));
      map?.setPaintProperty(
        `${id}-outline`,
        'line-offset',
        strokeOffset(style)
      );
    }
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    style.fillColor,
    style.fillOpacity,
    style.strokeColor,
    style.strokeOpacity,
    style.strokeWeight,
    style.strokeOffset
  ]);

  useEffect(() => {
    addLayer();
    setData();

    isMountedRef.current = false;

    return () => {
      // The Map interface does not have a style property, but the Mapbox GL JS map
      // object does.
      // @ts-expect-error
      if (map && Boolean(map.style) && map.getLayer(layer.id)) {
        map.removeLayer(layer.id);
        map.removeLayer(layerOutline.id);
        map.removeSource(sourceName);
      }
    };
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default PolygonCircle;
